import type { QueryClient } from '@tanstack/react-query';
import { useParams, type LoaderFunctionArgs } from 'react-router-dom';

import { FullScreenLoader } from '@/components/loaders/full-screen-loader';
import { CommentatorsLayout } from '@/components/ui/layouts/commentators-layout';
import {
  getCourtQueryOptions,
  useCourt,
} from '@/features/courts/api/get-court';
import { useCourts } from '@/features/courts/api/get-courts';
import { useDivisions } from '@/features/divisions/api/get-divisions';
import type { Participant } from '@/types/api';

export const lineupsLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const courtId = params.courtId as string;

    const query = getCourtQueryOptions(courtId);

    return (
      queryClient.getQueryData(query.queryKey) ??
      (await queryClient.fetchQuery(query))
    );
  };

const ParticipantItem = ({ participant }: { participant: Participant }) => {
  return (
    <div className="flex items-center gap-4 border-b border-gray-200 px-3 dark:border-gray-700">
      <div className="flex flex-1 items-center justify-between">
        <span className="w-12 text-lg font-semibold">
          {isNaN(parseInt(participant.player_number))
            ? 'Staff'
            : participant.player_number}
        </span>
        <span className="text-lg font-medium">
          {participant.given_name} {participant.family_name}
        </span>
      </div>
    </div>
  );
};

const TeamLineup = ({
  teamName,
  participants,
}: {
  teamName: string;
  participants: Participant[];
}) => {
  const sortedParticipants = [...participants].sort((a, b) => {
    const aNum = parseInt(a.player_number);
    const bNum = parseInt(b.player_number);
    const aIsStaff = isNaN(aNum);
    const bIsStaff = isNaN(bNum);

    if (aIsStaff && !bIsStaff) return 1;
    if (!aIsStaff && bIsStaff) return -1;

    if (aIsStaff && bIsStaff) {
      return `${a.family_name} ${a.given_name}`.localeCompare(
        `${b.family_name} ${b.given_name}`,
      );
    }

    return aNum - bNum;
  });

  return (
    <div className="mb-8">
      <h3 className="mb-4 text-xl font-bold">{teamName}</h3>
      <div className="rounded-lg bg-white dark:bg-gray-900">
        {sortedParticipants.map((participant) => (
          <ParticipantItem key={participant.id} participant={participant} />
        ))}
      </div>
    </div>
  );
};

export const CommentatorsLineupsPage = () => {
  const params = useParams();
  const courtId = params.courtId as string;

  const divisionsQuery = useDivisions();
  const courtsQuery = useCourts();
  const courtQuery = useCourt({ courtId });

  if (
    divisionsQuery.isLoading ||
    courtsQuery.isLoading ||
    courtQuery.isLoading
  ) {
    return <FullScreenLoader />;
  }

  if (!divisionsQuery.data || !courtsQuery.data || !courtQuery.data) {
    return null;
  }

  const { divisions } = divisionsQuery.data;
  const { courts } = courtsQuery.data;
  const { court } = courtQuery.data;

  if (!court.active_match) {
    return (
      <CommentatorsLayout courts={courts} divisions={divisions}>
        <div className="mt-10 text-gray-700 dark:bg-gray-50">
          <div className="flex h-64 items-center justify-center text-xl text-gray-500">
            No active match on this court
          </div>
        </div>
      </CommentatorsLayout>
    );
  }

  const { active_match: match } = court;

  return (
    <CommentatorsLayout courts={courts} divisions={divisions}>
      <div className="mt-10 text-gray-700 dark:bg-gray-50">
        <div className="mb-6">
          <h2 className="text-2xl font-bold">{court.name}</h2>
          <p className="text-lg text-gray-600">{match.label}</p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <TeamLineup
            teamName={match.home_team.name}
            participants={match.home_team_participants}
          />
          <TeamLineup
            teamName={match.away_team.name}
            participants={match.away_team_participants}
          />
        </div>
      </div>
    </CommentatorsLayout>
  );
};
